import {globals} from '../extensions/globals-base'
import {Project} from '../extensions/project'
import {UserAccess} from '../extensions/user-access'
import {
  DataObjectId,
  getPermissionFromRole,
  permissionChildToObject,
  SandboxTree,
  PermissionsById
} from 'yougile-model'
import {ObjUtil} from 'yougile-util'
import {PermissionsChecker} from './permissions-checker'
import {SharedBoardsHelper} from './shared-boards-helper'
import {SyncEvents} from './sync-events'

export class PermissionsHelper {
  /**
   * Contains custom role permissions for current user.
   * key is companyId
   */
  private static cachedCustomRolesPermissions: Map<
    DataObjectId,
    PermissionsById
  > = new Map()

  public static init() {
    SyncEvents.addAnyListener(companyId => {
      PermissionsHelper.cachedCustomRolesPermissions.delete(companyId)
    })
  }

  public static getAllProjectsWithFullAccess(
    box: SandboxTree
  ): Set<DataObjectId> {
    const res: Set<DataObjectId> = new Set()
    const allProjects = Project.listProjects(box.companyId, 'all')
    allProjects.forEach(p => {
      if (
        p &&
        (PermissionsChecker.isAdmin() ||
          UserAccess.getRoleInProject(globals.currentUserId, p) === 'admin')
      ) {
        res.add(p.id)
      }
    })
    return res
  }

  public static canEditPermissions(
    box: SandboxTree,
    objId: DataObjectId
  ): boolean {
    const projectsWithAccess = PermissionsHelper.getAllProjectsWithFullAccess(
      box
    )
    const obj = box.get(objId)
    const proj = obj && box.getProjectFor(obj)
    return proj && projectsWithAccess.has(proj.id)
  }

  public static getRolePermissions(
    userId: DataObjectId,
    box: SandboxTree,
    objectId: DataObjectId
  ): PermissionsById {
    const proj = box.getProjectFor(objectId)
    if (!proj) {
      return {}
    }
    const role = UserAccess.getRoleInProject(userId, proj)
    if (!role) {
      return {}
    }
    const permissions =
      SharedBoardsHelper.getSharedRole(proj.id) ??
      getPermissionFromRole(proj, role, true)
    return permissions
  }

  public static getAllCustomRolesPermissions(
    userId: DataObjectId,
    box: SandboxTree
  ): PermissionsById {
    if (
      userId === globals.currentUserId &&
      PermissionsHelper.cachedCustomRolesPermissions.has(box.companyId)
    ) {
      return PermissionsHelper.cachedCustomRolesPermissions.get(box.companyId)
    }

    const projects = box.getChildrenHubs(box.companyId)
    const withCustomRole = projects.filter(p => {
      const key = p?.data?.userPermissions?.[userId]
      return !!p?.data?.roles?.[key]
    })
    const permissions: PermissionsById = {}
    withCustomRole.map(p => {
      const key = p.data.userPermissions[userId]
      const role = p.data.roles[key]
      permissions[p.id] = {
        type: 'project',
        objectId: p.id,
        permissions: role.permissions
      }
      ObjUtil.forEach(role.permissions?.children, (id: DataObjectId, perm) => {
        permissions[id] = permissionChildToObject(id, perm)
      })
    })
    if (userId === globals.currentUserId) {
      PermissionsHelper.cachedCustomRolesPermissions.set(
        box.companyId,
        permissions
      )
    }
    return permissions
  }
}
